import { TFunction, Trans, useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import {
	Button,
	Dropdown,
	Empty,
	Form,
	Input,
	Menu,
	Modal,
	Radio,
	Spin,
	Table,
	TablePaginationConfig,
} from 'antd';
import styles from './DownloadableContent.module.less';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api, {
	DLCStatus,
	DLCType,
	DownloadableContentSortField,
	DownloadableContentSummary,
	DownloadableContentSummaryPagedResult,
	SortOrder,
} from '../../../api';
import {
	getAPISorter,
	getCellValue,
	getShowTotal,
} from '../../../utils/tableUtils';
import Breadcrumb, { IRoutes } from '../../../components/Breadcrumb/Breadcrumb';
import { useSelector } from 'react-redux';
import { projectSelector } from '../../../redux/selectors/projectSelector';
import { History } from 'history';
import { SorterResult } from 'antd/lib/table/interface';
import { getFormattedDateWithTz } from 'utils/dateUtils';
import { FormProvider, useForm } from 'react-hook-form';
import FormItem from 'components/form/FormItem';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import OmniNotification from 'components/OmniNotification';

type SorterType =
	| SorterResult<DownloadableContentSummary>
	| SorterResult<DownloadableContentSummary>[];

const DefaultSorter = {
	order: 'descend' as unknown as SortOrder,
	column: {
		APISortField: DownloadableContentSortField.Name,
	} as any,
};

const getSortOrder = (
	sorterRef: SorterResult<DownloadableContentSummary>,
	field: DownloadableContentSortField,
) => {
	return (sorterRef?.column as any)?.APISortField === field
		? sorterRef.order
		: undefined;
};

export const getStatus = (t: TFunction, status?: DLCStatus) => {
	switch (status) {
		case DLCStatus.Draft:
			return t('downloadableContent.enmu.status.draft');
		case DLCStatus.Published:
			return t('downloadableContent.enmu.status.published');
		default:
			return '';
	}
};

export const getType = (t: TFunction, type?: DLCType) => {
	switch (type) {
		case DLCType.Durable:
			return t('downloadableContent.enmu.type.durable');
		case DLCType.Consumable:
			return t('downloadableContent.enmu.type.consumable');
		default:
			return '';
	}
};

const getValidationSchema = (t: TFunction, productId: string) =>
	yup.object().shape({
		name: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContent.table.columns.name'),
				}),
			)
			.max(
				80,
				t('validation.maxLength', {
					fieldName: t('downloadableContent.table.columns.name'),
					count: 80,
				}),
			),
		sku: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContent.table.columns.sku'),
				}),
			)
			.test(
				'validate-SKU',
				({ value }) =>
					t('downloadableContent.modal.error.nonuniqueSKU', { name: value }),
				async (value, context) => {
					if (isTriggeredBySubmit) {
						return api.validateSKUForDLC({
							sku: value,
							productId,
						});
					} else if (value !== originalApiName) {
						originalApiName = value!;
						return api.validateSKUForDLC({
							sku: value,
							productId,
						});
					} else {
						return true;
					}
				},
			)
			.matches(
				/^(?!.*[?;\/:@=&]).*$/,
				t('common.unsafeCharacters', {
					fieldName: t('downloadableContent.table.columns.sku'),
				}),
			)
			.matches(
				/^[\S]*$/,
				t('common.noSpaces', {
					fieldName: t('downloadableContent.table.columns.sku'),
				}),
			)
			.max(
				200,
				t('validation.maxLength', {
					fieldName: t('downloadableContent.table.columns.sku'),
					count: 200,
				}),
			),
	});

interface FormValues {
	name: string;
	sku: string;
	type: DLCType;
}

const defaultValues: FormValues = {
	name: '',
	sku: '',
	type: DLCType.Durable,
};

var originalApiName = '';
var isTriggeredBySubmit = false;

export default function DownloadableContent() {
	const { t } = useTranslation();
	const history = useHistory();
	const route = useRouteMatch();
	const [downloadableContent, setDownloadableContent] =
		useState<DownloadableContentSummaryPagedResult>();
	const [sorterRef, setSorterRef] = useState<SorterType>(DefaultSorter);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const projectInfo = useSelector(projectSelector);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const validationSchema: any = getValidationSchema(
		t,
		projectInfo?.productId as string,
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});

	const pageForm = useForm<FormValues>({
		mode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	const { control, handleSubmit, formState, reset } = pageForm;

	const watchEntireForm = pageForm.watch();

	useEffect(() => {
		isTriggeredBySubmit = false;
	}, [watchEntireForm]);

	useEffect(() => {
		handleTableChange(pagination, null, DefaultSorter);
	}, [projectInfo?.productId]);

	const handleTableChange = async (
		tablePagination: TablePaginationConfig,
		filters: any,
		sorter: SorterType,
	) => {
		setSorterRef(sorter);

		if (!projectInfo?.productId) {
			return;
		}

		const { sortOrder, sortField } = getAPISorter(sorter);

		setIsLoading(true);

		api
			.getAllDlcForProduct(
				tablePagination.current as number,
				tablePagination.pageSize as number,
				projectInfo?.productId,
				sortField,
				sortOrder,
			)
			.then((res) => {
				setIsLoading(false);
				setDownloadableContent(res);
				setPagination({
					...pagination,
					...tablePagination,
					total: res.totalCount || 0,
				});
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: `game-services`,
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: '',
				breadcrumbName: t('downloadableContent.title'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	const getColumns = (
		t: TFunction,
		sorterRef: SorterResult<DownloadableContentSummary>,
		history: History,
		url: string,
	) => {
		return [
			{
				title: t('downloadableContent.table.columns.name'),
				render: (record: DownloadableContentSummary) =>
					getCellValue(record.name),
				ellipsis: true,
				sorter: true,
				sortOrder: getSortOrder(sorterRef, DownloadableContentSortField.Name),
				APISortField: DownloadableContentSortField.Name,
			},
			{
				title: t('downloadableContent.table.columns.sku'),
				render: (record: DownloadableContentSummary) =>
					getCellValue(record.sku),
				ellipsis: true,
			},
			{
				title: t('downloadableContent.table.columns.type'),
				render: (record: DownloadableContentSummary) =>
					getCellValue(getType(t, record.type)),
				ellipsis: true,
				width: 150,
			},
			{
				title: t('downloadableContent.table.columns.price'),
				render: (record: DownloadableContentSummary) =>
					getCellValue(record.price),
				ellipsis: true,
				width: 150,
			},
			{
				title: t('downloadableContent.table.columns.dateCreated'),
				render: (record: DownloadableContentSummary) => {
					return getCellValue(getFormattedDateWithTz(record.createdDate));
				},
				ellipsis: true,
				sorter: true,
				sortOrder: getSortOrder(
					sorterRef,
					DownloadableContentSortField.DateCreated,
				),
				APISortField: DownloadableContentSortField.DateCreated,
			},
			{
				title: t('downloadableContent.table.columns.status'),
				render: (record: DownloadableContentSummary) =>
					getCellValue(getStatus(t, record.status)),
				ellipsis: true,
				width: 150,
			},
			{
				title: t('downloadableContent.table.actions'),
				render: (record: DownloadableContentSummary) => {
					return (
						<>
							<span
								className={styles.action}
								onClick={() => {
									onRowClick(record);
								}}
							>
								{t('common.Edit')}
							</span>
						</>
					);
				},
				width: 150,
			},
		];
	};

	const columns = useMemo(
		() =>
			getColumns(
				t,
				sorterRef as SorterResult<DownloadableContentSummary>,
				history,
				route.url,
			),
		[t, sorterRef, history, getColumns],
	);

	const submitForm = async () => {
		isTriggeredBySubmit = true;
		await handleSubmit(async (formValues: FormValues) => {
			setIsSubmitting(true);
			api
				.initializeForDLC({
					name: formValues.name,
					sku: formValues.sku,
					type: formValues.type,
					productId: projectInfo?.productId,
				})
				.then(() => {
					OmniNotification.success({
						message: t('downloadableContent.modal.success.message'),
						description: t('downloadableContent.modal.success.message'),
					});
					reset();
					setShowModal(false);
					handleTableChange(pagination, null, DefaultSorter);
				})
				.catch(() => {
					OmniNotification.error({
						message: t('notification.error.title'),
						description: t('api.defaultError'),
					});
				})
				.finally(() => {
					setIsSubmitting(false);
					setShowModal(false);
				});
		})();
	};

	const onRowClick = (record: DownloadableContentSummary) => {
		history.push(`${route.url}/edit/${record.id}`);
	};

	return (
		<>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<h2 className="h2" data-testid="page-title">
					{t('downloadableContent.title')}
				</h2>
				<div className="page-head">
					<div className={styles.subTitle} data-testid="page-sub-title">
						{t('downloadableContent.subTitle')}
					</div>
					<div className={styles.buttonContainer}>
						<Button
							type="primary"
							data-testid="create-leaderboard-button"
							onClick={() => {
								setShowModal(true);
							}}
						>
							{t('downloadableContent.add')}
						</Button>
					</div>
				</div>
				<div>
					<Table
						className="table"
						size="middle"
						rowKey="id"
						onChange={handleTableChange}
						loading={isLoading}
						rowClassName={styles.tableRow}
						columns={columns}
						dataSource={downloadableContent?.items}
						sortDirections={['descend', 'ascend', 'descend']}
						data-testid="downloadableContentTable"
						pagination={pagination}
					/>
				</div>
			</div>
			<Modal
				visible={showModal}
				title={t('downloadableContent.modal.title')}
				maskClosable={false}
				onCancel={() => {
					reset();
					setShowModal(false);
				}}
				destroyOnClose={true}
				className={styles.modal}
				footer={[
					<Button
						key="back"
						className={'color-dark'}
						onClick={() => {
							reset();
							setShowModal(false);
						}}
					>
						{t('common.cancel')}
					</Button>,
					<Button
						key="submit"
						type="primary"
						loading={isSubmitting}
						onClick={submitForm}
					>
						{t('common.Create')}
					</Button>,
				]}
			>
				<FormProvider {...pageForm}>
					<Form data-testid="form" layout="vertical">
						<FormItem
							name="name"
							label={t('downloadableContent.table.columns.name')}
							control={control}
							className={cx('has-counter')}
							render={({ field }) => (
								<>
									<Input {...field} />
									<span className="form-counter">
										{field.value?.length || 0}/80
									</span>
								</>
							)}
						/>
						<FormItem
							name="sku"
							label={t('downloadableContent.table.columns.sku')}
							control={control}
							className={cx('has-counter')}
							render={({ field }) => (
								<>
									<Input {...field} />
									<span className="form-counter">
										{field.value?.length || 0}/200
									</span>
								</>
							)}
						/>
						<FormItem
							name="type"
							label={t('downloadableContent.table.columns.type')}
							control={control}
							render={({ field }) => (
								<Radio.Group {...field}>
									<Radio value={DLCType.Durable}>
										{getType(t, DLCType.Durable)}
									</Radio>
									<Radio value={DLCType.Consumable}>
										{getType(t, DLCType.Consumable)}
									</Radio>
								</Radio.Group>
							)}
						/>
					</Form>
				</FormProvider>
			</Modal>
		</>
	);
}
